import { useContext, useEffect, useState } from "react"
import { Badge, Chip, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton, Link as MuiLink, Stack, Tooltip } from "@mui/material"
import {
    Button,
    Box,
    CircularProgress,
    Divider,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material"
import { APIError } from "../../../../app/appTypes"
import { useAppDispatch, useAppSelector } from "../../../../app/hooks"
import { impossible } from "../../../../app/Utils"
import { showError } from "../../../notifications/notificationsSlice"
import { DocumentSectionHeader } from "../../components/DocumentSectionHeader"
import { SectionDeleteContext } from "../../DocumentContent"
import { Attachements } from "../../../urs/Attachements"
import { BlobResourceType } from "../../blobResourceTypes"
import { loadManyBlobs, remarksRequiredForDocumentStatus, selectAttachements } from "../../attachementsSlice"
import { updateDocSection } from "../../documentsApi"
import { isDocEditable, sectionSelectors, selectDocument } from "../../documentSlice"
import { DocumentSectionDeleteDialog } from "../../utils/DeleteSectionDialog"
import { DocumentReference, loadDocumentReferences } from "./docReferencesSectionApi"
import { Localized } from "@fluent/react"
import { AddEditDocReferenceDialog } from "./dialogs/AddEditDocReferenceDialog"
import { Edit, Delete, Add } from "@mui/icons-material";
import { DeleteButton } from "../../../../app/components/DeleteButton"
import { CancelButton } from "../../../../app/components/CancelButton"
import AttachmentIcon from '@mui/icons-material/Attachment'


export const DocReferencesSectionView = (props: { sectionId: string }) => {
    const { sectionId } = props
    const dispatch = useAppDispatch()
    const deleteSection = useContext(SectionDeleteContext)
    const section = useAppSelector(state => sectionSelectors.selectById(state, sectionId)) ?? impossible()
    const { header } = useAppSelector(selectDocument)?.doc ?? impossible()
    const isEditable = useAppSelector(isDocEditable)
    const { numeration } = section

    const [error, setError] = useState<APIError | null>(null)
    const [del, setDel] = useState(false)
    const [deleting, setDeleting] = useState(false)
    const [items, setItems] = useState<DocumentReference[]>([])
    const [loading, setLoading] = useState(false)
    const [createDialogOpen, setCreateDialogOpen] = useState(false)
    const [editItem, setEditItem] = useState<DocumentReference | null>(null)
    const [deleteItem, setDeleteItem] = useState<DocumentReference | null>(null)
    const [attachmentsItem, setAttachmentsItem] = useState<DocumentReference | null>(null)
    const attachments = useAppSelector(selectAttachements)

    useEffect(() => {
        const load = async () => {
            setLoading(true)
            try {
                const result = await dispatch(loadDocumentReferences({ documentId: header.id })).unwrap()

                const guids = result.map(item => item.guid).filter(Boolean)
                if (guids.length > 0) {
                    await dispatch(loadManyBlobs(guids)).unwrap()
                }
                setItems(result)
            } catch {
                dispatch(showError("error"))
            } finally {
                setLoading(false)
            }
        }

        load()
    }, [dispatch, header.id])

    const handleApprove = async (newName: string) => {
        if (!newName) {
            return
        }

        try {
            await dispatch(updateDocSection({ ...section, name: newName })).unwrap()
        } catch (err) {
            setError(err as APIError)
        }
    }

    const handleDeleteClick = () => setDel(true)

    const handleConfirmDeleteClick = async () => {
        try {
            setDeleting(true)
            deleteSection(sectionId)
        } catch {
            dispatch(showError("error"))
        } finally {
            setDeleting(false)
        }
    }

    const handleCancelDelClick = () => setDel(false)

    const handleOpenCreateDialog = () => setCreateDialogOpen(true)

    const handleCloseCreateDialog = () => setCreateDialogOpen(false)

    const handleCreateSuccess = (documentReference: DocumentReference) => {
        setItems(current => [...current, documentReference])
        setCreateDialogOpen(false)
    }

    const openEditDialog = (documentReference: DocumentReference) => {
        setEditItem(documentReference)
    }

    const handleCloseEditDialog = () => setEditItem(null)

    const handleEditSuccess = (documentReference: DocumentReference) => {
        setItems(current => current.map(item => item.id === documentReference.id ? documentReference : item))
        setEditItem(null)
    }

    return (
        <>
            <DocumentSectionHeader
                numeration={numeration}
                name={section.name}
                isEditable={isEditable}
                error={error}
                onSaveName={handleApprove}
                onDeleteClick={handleDeleteClick}
            />

            {del && (
                <DocumentSectionDeleteDialog
                    deleting={deleting}
                    hasSubSections={section.subSectionsIds.length > 0}
                    onConfirm={handleConfirmDeleteClick}
                    onCancel={handleCancelDelClick}
                />
            )}

            <Divider />


            {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
                    <CircularProgress size={24} />
                </Box>
            ) : (
                <TableContainer component={Paper} sx={{ mt: 1 }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ width: 64 }}><Localized id="lp">_No</Localized></TableCell>
                                <TableCell sx={{ width: 180 }}><Localized id="document-type">ReferenceType</Localized></TableCell>
                                <TableCell sx={{ width: 180 }}><Localized id="number">ReferenceNumber</Localized></TableCell>
                                <TableCell sx={{ minWidth: 220 }}><Localized id="title">Title</Localized></TableCell>
                                <TableCell sx={{ minWidth: 220 }}>Link</TableCell>
                                <TableCell sx={{ minWidth: 260 }}><Localized id="description">Description</Localized></TableCell>
                                {isEditable && <TableCell sx={{ width: 100 }} />}

                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {items.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={6}>
                                        <Typography variant="body2" color="text.secondary">
                                            <Localized id="document-no-references-warning">_Brak powiązań dokumentów.</Localized>
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                items.map((item, index) => (
                                    <TableRow key={item.id} hover>
                                        <TableCell>{index + 1}</TableCell>
                                        <TableCell>{item.referenceType ?? ""}</TableCell>
                                        <TableCell>{item.referenceNumber ?? ""}</TableCell>
                                        <TableCell>{item.title ?? item.referencedDocumentTitle ?? ""}</TableCell>
                                        <TableCell>
                                            {item.url ? (
                                                <MuiLink href={item.url} target="_blank" rel="noopener noreferrer" underline="hover">
                                                    {item.url}
                                                </MuiLink>
                                            ) : (
                                                ""
                                            )}
                                        </TableCell>
                                        <TableCell>{item.description ?? ""}</TableCell>
                                        {isEditable &&
                                            <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                                <Badge
                                                    color="info"
                                                    badgeContent={attachments.entities[item.guid]?.blobs?.length ?? 0}
                                                    invisible={(attachments.entities[item.guid]?.blobs?.length ?? 0) === 0}
                                                >
                                                    <Tooltip title={<Localized id="attachements">Załączniki</Localized>}>
                                                        <IconButton size="small" onClick={() => setAttachmentsItem(item)}>
                                                            <AttachmentIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Badge>
                                                <IconButton size="small" onClick={() => openEditDialog(item)}>
                                                    <Edit fontSize="small" />
                                                </IconButton>
                                                <IconButton size="small" onClick={() => setDeleteItem(item)}>
                                                    <Delete fontSize="small" />
                                                </IconButton>
                                            </TableCell>
                                        }
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>

                    <Button
                        variant="outlined"
                        color="primary"
                        startIcon={<Add />}
                        size="small"
                        sx={{ m: 1 }}
                        onClick={handleOpenCreateDialog}
                        disabled={!isEditable}
                    >
                        <Localized id="add">
                            <span>_Add</span>
                        </Localized>
                    </Button>

                </TableContainer>

            )}

            {createDialogOpen && (
                <AddEditDocReferenceDialog
                    documentId={header.id}
                    sectionId={sectionId}
                    documentReference={null}
                    mode="create"
                    onSuccessfulSave={handleCreateSuccess}
                    onCancelClick={handleCloseCreateDialog}
                />
            )}

            {editItem && (
                <AddEditDocReferenceDialog
                    documentId={header.id}
                    sectionId={sectionId}
                    documentReference={editItem}
                    mode="edit"
                    onSuccessfulSave={handleEditSuccess}
                    onCancelClick={handleCloseEditDialog}
                />
            )}

            {attachmentsItem && (
                <Dialog open={true} fullWidth maxWidth="md" onClose={() => setAttachmentsItem(null)}>
                    <DialogTitle>
                        <Localized id="attachements">Załączniki</Localized>
                    </DialogTitle>
                    <DialogContent>
                        <Paper
                            sx={{
                                mb: 1,
                                mt: 1,
                                border: "1px solid",
                                borderColor: "divider",
                                borderLeft: "3px solid",
                                borderLeftColor: "primary.main",
                                overflow: "hidden",
                                boxShadow: "none",
                            }}
                        >
                            <Box
                                sx={{
                                    display: "grid",
                                    gridTemplateColumns: "92px 1fr",
                                    borderBottom: "1px solid",
                                    borderColor: "divider",
                                }}
                            >
                                <Box
                                    sx={{
                                        px: 1,
                                        py: 0.5,
                                        bgcolor: "action.hover",
                                        borderRight: "1px solid",
                                        borderColor: "divider",
                                    }}
                                >
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{
                                            fontWeight: 600,
                                            textTransform: "uppercase",
                                            letterSpacing: 0.4,
                                        }}
                                    >
                                        <Localized id="type">_Type</Localized>
                                    </Typography>
                                </Box>

                                <Box sx={{ px: 1, py: 0.5, minWidth: 0 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                        {attachmentsItem.referenceType || "-"}
                                    </Typography>
                                </Box>
                            </Box>

                            <Box
                                sx={{
                                    display: "grid",
                                    gridTemplateColumns: "92px 1fr",
                                    borderBottom: "1px solid",
                                    borderColor: "divider",
                                }}
                            >
                                <Box
                                    sx={{
                                        px: 1,
                                        py: 0.5,
                                        bgcolor: "action.hover",
                                        borderRight: "1px solid",
                                        borderColor: "divider",
                                    }}
                                >
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{
                                            fontWeight: 600,
                                            textTransform: "uppercase",
                                            letterSpacing: 0.4,
                                        }}
                                    >
                                        <Localized id="number">_Number</Localized>
                                    </Typography>
                                </Box>

                                <Box sx={{ px: 1, py: 0.5, minWidth: 0 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                        {attachmentsItem.referenceNumber || "-"}
                                    </Typography>
                                </Box>
                            </Box>

                            <Box
                                sx={{
                                    display: "grid",
                                    gridTemplateColumns: "92px 1fr",
                                }}
                            >
                                <Box
                                    sx={{
                                        px: 1,
                                        py: 0.5,
                                        bgcolor: "action.hover",
                                        borderRight: "1px solid",
                                        borderColor: "divider",
                                    }}
                                >
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{
                                            fontWeight: 600,
                                            textTransform: "uppercase",
                                            letterSpacing: 0.4,
                                        }}
                                    >
                                        <Localized id="title">_Title</Localized>
                                    </Typography>
                                </Box>

                                <Box sx={{ px: 1, py: 0.5, minWidth: 0 }}>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontWeight: 500,
                                            whiteSpace: "normal",
                                            lineHeight: 1.4,
                                        }}
                                    >
                                        {attachmentsItem.title || "-"}
                                    </Typography>
                                </Box>
                            </Box>
                        </Paper>
                        <Divider sx={{ mb: 2 }} />

                        <Box
                            sx={{
                                mt: 2,
                                mb: 1,
                                px: 1.25,
                                py: 0.75,
                                borderRadius: 1,
                                bgcolor: "action.hover",
                                border: "1px solid",
                                borderColor: "divider",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                            }}
                        >
                            <Typography
                                variant="caption"
                                sx={{
                                    fontWeight: 700,
                                    textTransform: "uppercase",
                                    letterSpacing: 0.5,
                                    color: "text.secondary",
                                }}
                            >
                                <Localized id="attachments-list">Lista załączników</Localized>
                            </Typography>
                        </Box>

                        <Box
                            sx={{
                                border: "1px solid",
                                borderColor: "divider",
                                borderRadius: 1,
                                p: 1.5,
                                bgcolor: "background.paper",
                            }}
                        >
                            <Attachements
                                resourceId={attachmentsItem.guid}
                                resourceType={BlobResourceType.DocumentReference}
                                parentResourceId={header.guid}
                                parentResourceType={BlobResourceType.Document}
                                canDelete={isEditable}
                                canUpload={isEditable}
                                requireRemarks={remarksRequiredForDocumentStatus(header.status)}
                            />
                        </Box>

                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setAttachmentsItem(null)}>
                            <Localized id="close">Zamknij</Localized>
                        </Button>
                    </DialogActions>
                </Dialog>
            )}

            <Dialog open={deleteItem !== null} onClose={() => setDeleteItem(null)}>
                <DialogContent>
                    <DialogContentText>
                        <Localized id="confirm-delete">Do you want to delete?</Localized>
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <DeleteButton onClick={handleConfirmDeleteClick} loading={deleting} />
                    <CancelButton onClick={() => setDeleteItem(null)} loading={deleting} />
                </DialogActions>
            </Dialog>
        </>
    )
}
